import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  TemplateRef,
  inject,
} from '@angular/core';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { MobileExplainTooltipSheetService } from 'src/services/mobile-explain-tooltip-sheet.service';
import { desktopWidthBreakpoint } from 'src/util/constants';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[mobileExplainTooltipSheet]',
  standalone: false,
})
export class MobileExplainTooltipSheetDirective implements AfterViewInit, OnDestroy {
  private tooltip = inject(NzTooltipDirective);
  private sheet = inject(MobileExplainTooltipSheetService);
  private el = inject(ElementRef<HTMLElement>);
  private ngZone = inject(NgZone);

  @Input() public learnMoreLink: string | null = null;
  @Input() public learnMoreRoute: string | string[] | null = null;

  private mediaQuery = window.matchMedia(`(max-width: ${desktopWidthBreakpoint - 1}px)`);
  private desktopTrigger: 'click' | 'focus' | 'hover' | null = 'hover';

  private onMediaChange = (): void => this.applyMobileMode();

  private onClickCapture = (event: Event): void => {
    if (!this.isMobile()) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    this.ngZone.run(() => this.openSheet());
  };

  public ngAfterViewInit(): void {
    this.desktopTrigger = this.tooltip.trigger ?? 'hover';
    this.applyMobileMode();
    this.mediaQuery.addEventListener('change', this.onMediaChange);
    this.el.nativeElement.addEventListener('click', this.onClickCapture, true);
  }

  public ngOnDestroy(): void {
    this.mediaQuery.removeEventListener('change', this.onMediaChange);
    this.el.nativeElement.removeEventListener('click', this.onClickCapture, true);
  }

  private isMobile(): boolean {
    return this.mediaQuery.matches;
  }

  private applyMobileMode(): void {
    this.tooltip.trigger = this.isMobile() ? null : this.desktopTrigger;
    (this.tooltip as unknown as { registerTriggers: () => void }).registerTriggers();
    if (this.isMobile()) {
      this.tooltip.hide();
    }
  }

  private openSheet(): void {
    const title = this.tooltip.title ?? this.tooltip.directiveTitle;
    if (!title) return;
    this.tooltip.hide();
    this.sheet.open(title as TemplateRef<void> | string, this.learnMoreLink, this.learnMoreRoute);
  }
}
